/**
 * Tool Metadata Step (Wizard step 2).
 *
 * Lets the user confirm/edit the tool name and category. The tool name is
 * pre-filled from auto-inference. `toolType` is fixed to
 * UNIVERSAL_STORED_PROCEDURE (read-only) for v1.
 *
 * The stored-procedure check is advisory only. It never blocks navigation.
 */

import React, { useCallback, useState } from 'react';
import {
  SpaceBetween,
  Container,
  Header,
  FormField,
  Input,
  Select,
  Button,
  Alert,
  Box,
  StatusIndicator,
} from '@amzn/awsui-components-react';

import { checkProcedure, installProcedures, OnboardingApiError } from '../onboardingApi';
import { CheckProcedureResponse, InstallProceduresResponse, OnboardingConfig } from '../types';
import { OnboardingAction, SECRET_ENV_OPTIONS, SecretEnv } from '../onboardingReducer';
import { SECRET_ARN_PATTERN, TOOL_NAME_PATTERN } from '../onboardingValidators';

export interface ToolMetadataStepProps {
  config: OnboardingConfig;
  dispatch: React.Dispatch<OnboardingAction>;
  backendAvailable?: boolean;
}

/** Inline (field-level) validation for the tool name. */
function toolNameError(toolName: string): string | null {
  if (!toolName.trim()) return 'Tool name is required.';
  if (!TOOL_NAME_PATTERN.test(toolName)) {
    return 'Must start with a letter and contain only letters, numbers, and underscores.';
  }
  return null;
}

const ToolMetadataStep: React.FC<ToolMetadataStepProps> = ({
  config,
  dispatch,
  backendAvailable = false,
}) => {
  const [checkEnv, setCheckEnv] = useState<SecretEnv>('test');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckProcedureResponse | null>(null);
  const [installing, setInstalling] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);
  const [installResult, setInstallResult] = useState<InstallProceduresResponse | null>(null);

  const nameError = toolNameError(config.toolName);
  const categoryError = config.category.trim() ? null : 'Category is required.';
  // Trim ONCE at the source and send the trimmed value. SECRET_ARN_PATTERN ends in
  // `.+$`, so a copy-pasted ARN with surrounding whitespace passes the gate; POSTing
  // the raw padded value then fails server-side (Secrets Manager rejects the padded
  // ARN). Trimming here fixes both call sites (check + install) at once and keeps
  // the gate correct, matching the sibling YamlPreviewStep.
  const secretArn = config.database.secretArns[checkEnv].trim();
  const canCheckProcedure = SECRET_ARN_PATTERN.test(secretArn);

  const handleCheck = useCallback(async () => {
    if (!canCheckProcedure) return;

    setChecking(true);
    setError(null);
    setResult(null);
    // A fresh check invalidates any prior install outcome (which described a
    // previous check's state), so clear it.
    setInstallError(null);
    setInstallResult(null);

    try {
      // The universal stored procedure lives in a fixed schema (public on FDA),
      // independent of the user's data-table schema. Omit schema_name so the
      // backend searches every schema reachable by the secret and reports where
      // it found the procedure — pinning to the data-table schema falsely
      // reports "missing" when the table lives outside the SP's schema.
      const response = await checkProcedure({
        secret_arn: secretArn,
        procedure_name: config.storedProcedure,
      });
      setResult(response);
    } catch (err) {
      setError(
        err instanceof OnboardingApiError ? err.message : 'Procedure check failed.',
      );
    } finally {
      setChecking(false);
    }
  }, [canCheckProcedure, config.storedProcedure, secretArn]);

  const handleInstall = useCallback(async () => {
    if (!canCheckProcedure) return;

    setInstalling(true);
    setInstallError(null);
    setInstallResult(null);

    try {
      // Install into the user's public schema (the backend fixes the schema and
      // uses vendored DDL — no schema/procedure input is interpolated into SQL).
      const response = await installProcedures({
        secret_arn: secretArn,
        procedure_name: config.storedProcedure,
      });
      setInstallResult(response);
      // The backend already re-ran the check post-install and returned `verified`,
      // so derive the check result from the SAME response instead of a second
      // round-trip. One call means the indicator and the install banner can never
      // disagree, and there is no separate recheck that could throw and leave a
      // green "installed" banner next to a stale red "not found" indicator.
      //
      // On verified=false we must NOT reuse response.installedProcedures for the
      // missing set — that is what was just CREATED, so the "not found: …"
      // indicator would name the procedures we just installed. The install
      // response carries no true missing set, so keep the missing set from the
      // pre-install check (`result.missingProcedures`) — still a correct upper
      // bound on what's absent, and it keeps the indicator's list non-empty rather
      // than rendering an empty "not found:" string.
      setResult((prev) => ({
        exists: response.verified,
        procedureName: config.storedProcedure,
        foundSchemas: response.verified ? [response.schema] : [],
        missingProcedures: response.verified ? [] : (prev?.missingProcedures ?? []),
      }));
    } catch (err) {
      setInstallError(
        err instanceof OnboardingApiError
          ? err.message
          : 'Failed to install the stored procedures. Check the connection and try again.',
      );
    } finally {
      setInstalling(false);
    }
  }, [canCheckProcedure, config.storedProcedure, secretArn]);

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Tool Metadata</Header>}>
        <SpaceBetween size="l">
          <FormField
            label="Tool name"
            description="Pre-filled from the parsed schema. Edit if needed."
            errorText={nameError ?? undefined}
          >
            <Input
              value={config.toolName}
              onChange={({ detail }) =>
                dispatch({ type: 'SET_METADATA_FIELD', field: 'toolName', value: detail.value })
              }
              placeholder="e.g. devices_daily_actuals"
              ariaLabel="Tool name"
            />
          </FormField>

          <FormField label="Tool type" description="Fixed for v1 onboarding.">
            <Input value={config.toolType} readOnly disabled ariaLabel="Tool type" />
          </FormField>

          <FormField
            label="Category"
            description="Logical grouping shown in Bluey."
            errorText={categoryError ?? undefined}
          >
            <Input
              value={config.category}
              onChange={({ detail }) =>
                dispatch({ type: 'SET_METADATA_FIELD', field: 'category', value: detail.value })
              }
              placeholder="e.g. Finance"
              ariaLabel="Category"
            />
          </FormField>
        </SpaceBetween>
      </Container>

      {backendAvailable && (
        <Container header={<Header variant="h2">Stored procedure</Header>}>
          <SpaceBetween size="l">
            <FormField
              label="Check using"
              description="Choose which environment secret ARN to check against Redshift."
            >
              <Select
                selectedOption={SECRET_ENV_OPTIONS.find((option) => option.value === checkEnv) ?? null}
                // Locked while a check or install is in flight: switching the env
                // mid-request would let the in-flight promise resolve and paint a
                // result/banner computed for the OLD secret beneath the new
                // selection. Disabling here is what prevents that stale-env race,
                // since an already-launched fetch can't be cancelled.
                disabled={checking || installing}
                onChange={({ detail }) => {
                  setCheckEnv((detail.selectedOption.value as SecretEnv) ?? 'test');
                  setError(null);
                  setResult(null);
                  setInstallError(null);
                  setInstallResult(null);
                }}
                options={SECRET_ENV_OPTIONS}
                ariaLabel="Stored procedure environment"
              />
            </FormField>

            <SpaceBetween size="s" direction="horizontal">
              <Button
                variant="primary"
                loading={checking}
                // Also disabled during an install: the two handlers write the same
                // result/install state, so letting a check fire mid-install would
                // race them and could transiently show a contradictory indicator.
                disabled={!canCheckProcedure || installing}
                onClick={handleCheck}
              >
                Check procedure
              </Button>
              {result?.exists && (
                <StatusIndicator type="success">
                  {result.foundSchemas.length > 0
                    ? `Procedure installed in schema ${result.foundSchemas.join(', ')}`
                    : 'Procedure is installed'}
                </StatusIndicator>
              )}
              {result && !result.exists && (
                <StatusIndicator type="error">
                  {`Stored procedure(s) not found: ${result.missingProcedures.join(', ')}`}
                </StatusIndicator>
              )}
            </SpaceBetween>

            {result && !result.exists && (
              <Alert type="warning" header="Stored procedure not installed">
                <SpaceBetween size="s">
                  <Box variant="p">
                    The Bluey runtime calls <Box variant="code">{config.storedProcedure}</Box> to
                    execute this tool, and that procedure in turn calls its logging procedure. The
                    following required procedure(s) were not found in any schema reachable by the
                    selected secret, so the tool will fail at runtime until your database
                    administrator installs them (and their dependency objects) on this cluster:
                  </Box>
                  <ul>
                    {result.missingProcedures.map((name) => (
                      <li key={name}>
                        <Box variant="code">{name}</Box>
                      </li>
                    ))}
                  </ul>
                  <Box variant="p">Your DBA also needs, on this Redshift cluster:</Box>
                  <ul>
                    <li>
                      the metric-catalog tables (
                      <Box variant="code">bluey_input.meta_prophecy_metrics_catalog</Box> and
                      {' '}
                      <Box variant="code">bluey_input.meta_prophecy_derived_metrics_catalog</Box>);
                    </li>
                    <li>
                      the <Box variant="code">bluey_output</Box> schema the procedure writes to.
                    </li>
                  </ul>
                  <Box variant="p">
                    You can install the procedure(s) into the <Box variant="code">public</Box> schema
                    on this cluster now, using the selected secret. This creates{' '}
                    <Box variant="code">{config.storedProcedure}</Box>, its logging procedure, and the
                    log table (it does not create the metric-catalog tables or the{' '}
                    <Box variant="code">bluey_output</Box> schema — do those separately). Re-running is
                    safe. Alternatively, the install bundle (DDL) is owned by the FDA team if you
                    prefer a manual install. This check is advisory: you can continue onboarding and
                    finish the install before the tool is promoted out of the test phase.
                  </Box>
                  <SpaceBetween size="s" direction="horizontal">
                    <Button
                      variant="primary"
                      loading={installing}
                      disabled={!canCheckProcedure || installing}
                      onClick={handleInstall}
                    >
                      Install procedures
                    </Button>
                  </SpaceBetween>
                </SpaceBetween>
              </Alert>
            )}

            {installResult && (
              // Green only when the backend's post-install re-check actually
              // confirmed the whole required set; otherwise this is a warning, so
              // the banner colour never contradicts the red indicator above.
              <Alert
                type={installResult.verified ? 'success' : 'warning'}
                header={
                  installResult.verified
                    ? 'Stored procedures installed'
                    : 'Installed, but the procedures are still not all present'
                }
              >
                <SpaceBetween size="xs">
                  <Box variant="p">
                    {`Installed into schema ${installResult.schema}: `}
                    {installResult.installedProcedures.join(', ')}.
                    {installResult.verified
                      ? ' The status above now confirms all required procedures are present.'
                      : ' A re-check still reports something missing — see the status above. This can' +
                        ' happen if the procedures were installed where the runtime search path' +
                        " can't reach them; contact the FDA team if it persists."}
                  </Box>
                </SpaceBetween>
              </Alert>
            )}

            {installError && (
              <Alert
                type="error"
                header="Could not install the stored procedures"
                dismissible
                onDismiss={() => setInstallError(null)}
                action={<Button onClick={handleInstall}>Retry</Button>}
              >
                {installError}
              </Alert>
            )}

            {error && (
              <Alert type="error" dismissible onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};

export default ToolMetadataStep;
